import React, { useEffect, useMemo, useState } from "react";
import { styled } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { userService } from "../../services";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import i18next from "i18next";
import dayjs from "dayjs";
import { Clear } from "@mui/icons-material";

const PREFIX = "infosClient";

const classes = {
  root: `${PREFIX}-root`,
  imageDesignTitle: `${PREFIX}-imageDesignTitle`,
  managerTag: `${PREFIX}-managerTag`,
  clientTypeStyle: `${PREFIX}-clientTypeStyle`,
  clientContainer: `${PREFIX}-clientContainer`,
  buyerContainer: `${PREFIX}-buyerContainer`,
  nameSurnameStyle: `${PREFIX}-nameSurnameStyle`,
  textfieldPosition: `${PREFIX}-textfieldPosition`,
  historyStyle: `${PREFIX}-historyStyle`,
  managerStyle: `${PREFIX}-managerStyle`,
  eventsHistoryContainer: `${PREFIX}-eventsHistoryContainer`,
  eventsHistoryElement: `${PREFIX}-eventsHistoryElement`,
};

const StyledDiv = styled("div")(({ theme }) => {
  return {
    [`& .${classes.root}`]: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      width: "100%",
    },
    [`& .${classes.imageDesignTitle}`]: {
      color: "black",
      fontSize: "24px",
      maxWidth: "650px",
      width: "100%",
      margin: "0 auto",
      textAlign: "center",
      backgroundColor: theme.palette.primary.main,
    },
    [`& .${classes.clientContainer}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      alignItems: "center",
      width: "100%",
      maxWidth: "500px",
    },
    [`& .${classes.managerTag}`]: {
      width: "100%",
      marginBottom: "15px",
      "& > div": {
        zIndex: "2",
      },
    },
    [`& .${classes.clientTypeStyle}`]: {
      width: "30%",
    },
    [`& .${classes.buyerContainer}`]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-evenly",
      width: "100%",
    },
    [`& .${classes.nameSurnameStyle}`]: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      width: "50%",
    },
    [`& .${classes.textfieldPosition}`]: {
      marginBottom: "15px",
    },
    [`& .${classes.historyStyle}`]: {
      width: "100%",
    },
    [`& .${classes.managerStyle}`]: {
      width: "100%",
      marginBottom: "1rem",
    },

    [`& .${classes.eventsHistoryContainer}`]: {
      display: "flex",
      flexDirection: "column",
      width: "100%",
      maxHeight: "600px",
      padding: "0.75rem",
      overflow: "auto",
      "&::-webkit-scrollbar": {
        width: "5px",
        height: "5px",
      },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: theme.palette.primary.main,
        borderRadius: "3px",
      },
      "&::-webkit-scrollbar-track": {
        backgroundColor: "rgb(181 154 79 / 58%)",
        bordeRadius: "10px",
      },
    },

    [`& .${classes.eventsHistoryElement}`]: {
      padding: "0.7rem",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-around",
      alignItems: "center",
      marginBottom: "1rem",
      position: "relative",
    },
  };
});

const newClientInfoEvent = {
  eventDate: dayjs().format("YYYY-MM-DD"),
  message: "",
};

export default function ClientInfos({ updateData, data, canUserModifyData }) {
  const { t } = useTranslation();

  const userSuperAdmin = useSelector(
    (state) => state.user?.currentUser?.account?.isSuperAdmin
  );
  const [allManagers, setAllManagers] = useState([]);
  const [login] = useState("");
  const [email] = useState("");
  const [platform] = useState("");
  const [sortModel] = useState({
    field: "createdDate",
    sort: "desc",
  });

  const buyerTypes = useMemo(
    () => [
      { key: "agency", name: t("Crm.buyerType.agency") },
      { key: "friend", name: t("Crm.buyerType.friend") },
      { key: "association", name: t("Crm.buyerType.association") },
      { key: "club", name: t("Crm.buyerType.club") },
      { key: "teammate", name: t("Crm.buyerType.teammate") },
      { key: "spouse", name: t("Crm.buyerType.spouse") },
      { key: "direct", name: t("Crm.buyerType.direct") },
      { key: "company", name: t("Crm.buyerType.company") },
      { key: "family", name: t("Crm.buyerType.family") },
      { key: "federation", name: t("Crm.buyerType.federation") },
      { key: "media", name: t("Crm.buyerType.media") },
      { key: "giftByADS", name: t("Crm.buyerType.giftByADS") },
      { key: "executive", name: t("Crm.buyerType.executive") },
      { key: "other", name: t("Crm.buyerType.other") },
    ],
    [t]
  );

  const CustomerPopper = (props) => {
    const modifiers = [
      {
        name: "flip",
        options: {
          fallbackPlacements: [],
        },
      },
    ];

    return (
      <Popper
        {...props}
        modifiers={modifiers}
        popperOptions={{
          placement: "bottom",
        }}
      />
    );
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    if (userSuperAdmin === 1) {
      userService
        .getOnlyAdmins(0, null, { login, email, platform, sortModel })
        .then((result) => {
          setAllManagers(() => result.result);
        });
    } else {
      return;
    }
  };

  const updateHistoryElement = (index, key, value) => {
    const tmp = [...data.buyer.history];
    tmp[index] = { ...tmp[index], [key]: value };
    updateData("buyer", "history", tmp);
  };

  const spliceHistoryElement = (index) => {
    const tmp = [...data.buyer.history];
    tmp.splice(index, 1);
    updateData("buyer", "history", tmp);
  };

  return (
    <StyledDiv>
      <div className={classes.root}>
        <div className={classes.clientContainer}>
          <Autocomplete
            label={t("Crm.adminClient.adminClientManager")}
            id="autocomplete-users-client-admin"
            disableClearable
            disabled={!canUserModifyData}
            PopperComponent={(props) => <CustomerPopper {...props} />}
            multiple
            options={allManagers}
            isOptionEqualToValue={(option, value) => {
              return option.id === value.id;
            }}
            getOptionLabel={(option) => option.login}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("Crm.adminClient.adminClientManager")}
                variant="outlined"
              />
            )}
            defaultValue={data.buyer.managerDetails}
            value={data.buyer.managerDetails}
            className={classes.managerStyle}
            onChange={(e, value) => {
              updateData("buyer", "managerDetails", value);
            }}
          />
          <div className={classes.buyerContainer}>
            <FormControl className={classes.clientTypeStyle}>
              <InputLabel id="buyer-type-label">
                {t("Crm.buyerType.buyerTypeLabel")}
              </InputLabel>
              <Select
                disabled={!canUserModifyData}
                labelId="buyer-type-label"
                id="type-acheteur-select"
                value={data.buyer.buyerType ? data.buyer.buyerType : ""}
                label={t("Crm.buyerType.buyerTypeLabel")}
                onChange={(e) =>
                  updateData("buyer", "buyerType", e.target.value)
                }
              >
                {buyerTypes.map((elem) => (
                  <MenuItem value={elem.key} key={elem.key}>
                    {elem.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <div className={classes.nameSurnameStyle}>
              <TextField
                disabled={!canUserModifyData}
                variant="outlined"
                className={classes.textfieldPosition}
                label={t("Crm.surname")}
                value={data.buyer.firstname ? data.buyer.firstname : ""}
                onChange={(e) =>
                  updateData("buyer", "firstname", e.target.value)
                }
              />
              <TextField
                disabled={!canUserModifyData}
                variant="outlined"
                className={classes.textfieldPosition}
                label={t("Crm.name")}
                value={data.buyer.lastname ? data.buyer.lastname : ""}
                onChange={(e) =>
                  updateData("buyer", "lastname", e.target.value)
                }
              />
            </div>
          </div>
          <TextField
            disabled={!canUserModifyData}
            multiline
            className={classes.historyStyle}
            rows={4}
            variant="outlined"
            label={t("Crm.story")}
            value={data.buyer.story ? data.buyer.story : ""}
            onChange={(e) => updateData("buyer", "story", e.target.value)}
          />
          {data.buyer?.history?.length > 0 && (
            <Box sx={{ textALign: "left", width: "100%" }}>
              <Typography variant="body1" sx={{ margin: "1rem 0" }}>
                {t("Crm.eventHistory")}
              </Typography>
              <Paper elevation={12} className={classes.eventsHistoryContainer}>
                {data.buyer.history.map((elem, index) => (
                  <Paper
                    elevation={20}
                    key={`history-elem-${index + 1}`}
                    className={classes.eventsHistoryElement}
                  >
                    <IconButton
                      disabled={!canUserModifyData}
                      onClick={() => spliceHistoryElement(index)}
                      sx={{
                        position: "absolute",
                        top: "0",
                        right: "0",
                        color: "#D3AF5F",
                      }}
                    >
                      <Clear />
                    </IconButton>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale={i18next.language.substring(0, 2)}
                    >
                      <DatePicker
                        disabled={!canUserModifyData}
                        label={t("Crm.eventDate")}
                        sx={{ marginBottom: "1rem" }}
                        onChange={(e) =>
                          updateHistoryElement(
                            index,
                            "eventDate",
                            dayjs(e).format("YYYY-MM-DD")
                          )
                        }
                        value={dayjs(elem.eventDate)}
                      />
                    </LocalizationProvider>
                    <TextField
                      disabled={!canUserModifyData}
                      value={elem.message}
                      onChange={(e) =>
                        updateHistoryElement(index, "message", e.target.value)
                      }
                      multiline
                      className={classes.historyStyle}
                      rows={2}
                      variant="outlined"
                      label={t("Crm.event")}
                    />
                  </Paper>
                ))}
              </Paper>
            </Box>
          )}
          <Button
            color="primary"
            variant="contained"
            disabled={!canUserModifyData}
            onClick={() => {
              console.log("test =>", data.buyer);
              updateData("buyer", "history", [
                ...(data.buyer.history || []),
                newClientInfoEvent,
              ]);
            }}
            sx={{ marginTop: "1rem" }}
          >
            {t("Crm.buttons.addEvent")}
          </Button>
        </div>
      </div>
    </StyledDiv>
  );
}
